/**
 * Client-side mirror of one room's live presence list — the WP5 `presence` broadcast, which is
 * always the *full* list (see `docs/specs/WP5-realtime.md`): "last wins", no version gate needed,
 * a new list simply replaces the old one for that room. Keyed by room so a session that has
 * visited more than one room during its lifetime doesn't lose the others' last-known presence
 * (a cheap thing to keep; `useRoomRealtime`'s cleanup calls `clearRoom` on unmount anyway).
 */
import { create } from 'zustand';
import type { RowKind } from '../lib/fieldValue';
import type { RoomRole } from '../lib/rooms';

export interface PresenceFocus {
  fieldId: number;
  rowKind: RowKind;
}

export interface Presence {
  userId: number;
  username: string;
  role: RoomRole;
  focus: PresenceFocus | null;
  since: string;
}

interface PresenceState {
  byRoom: Map<number, Presence[]>;
  setPresence: (roomId: number, list: Presence[]) => void;
  clearRoom: (roomId: number) => void;
}

export const usePresenceStore = create<PresenceState>((set, get) => ({
  byRoom: new Map(),

  setPresence: (roomId, list) => {
    const byRoom = new Map(get().byRoom);
    byRoom.set(roomId, list);
    set({ byRoom });
  },

  clearRoom: (roomId) => {
    const byRoom = new Map(get().byRoom);
    byRoom.delete(roomId);
    set({ byRoom });
  },
}));

// A stable empty-array reference: returning a fresh `[]` literal from the selector below would
// compare unequal to itself on every store update (zustand's default `Object.is` check), causing
// an extra re-render of every consumer each time *any* room's presence changes.
const EMPTY: Presence[] = [];

/** The live presence list for one room — `[]` until the first broadcast arrives. */
export function usePresence(roomId: number): Presence[] {
  return usePresenceStore((state) => state.byRoom.get(roomId) ?? EMPTY);
}
